
import { Component, Prop, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { isEqual } from 'lodash';
import Toast from '@/utils/toast';
import IconButton from '@/components/shared/suites/IconButton.vue';
import InfoBanner from '@/components/shared/suites/InfoBanner.vue';
import StandardModal from '@/components/shared/suites/StandardModal.vue';
import TextButton from '@/components/shared/suites/TextButton.vue';
import TextField from '@/components/shared/TextField.vue';
import WarningModal from '@/components/shared/suites/WarningModal.vue';

const namespace: string = 'auth';

@Component({
	components: {
		IconButton,
		InfoBanner,
		StandardModal,
		TextButton,
		TextField,
		WarningModal
	}
})
export default class AccountingCategoriesModal extends Vue {
	@Action('updateAccountingCategories', { namespace }) private updateAccountingCategories!: (updatedCategories: string[]) => Promise<void>;
	@Getter('getRestaurant', { namespace }) private restaurant!: Restaurant;
	@Prop({ required: true, type: Boolean }) private showModal!: boolean;

	private newCategory: string = '';
	private accountingCategories: string[] = [];
	private errorMessage: string = '';
	private isUpdating: boolean = false;
	private showWarningModal: boolean = false;

	private created(): void {
		this.accountingCategories = this.restaurant.accounting_categories ? [...this.restaurant.accounting_categories] : [];
	}

	/**
	 * Add a new accounting category to the list if it doesn't already exist
	 *
	 * @return {void}
	 */
	private addCategory(): void {
		if (!this.newCategory) {
			return;
		}
		if (this.accountingCategories.includes(this.newCategory)) {
			this.errorMessage = this.$t('settings.accounting_categories.error_category_not_unique');
			return;
		}

		this.accountingCategories.push(this.newCategory);
		this.newCategory = '';
	}

	/**
	 * Remove an accounting category from the list by index
	 *
	 * @param {number} index
	 * @return {void}
	 */
	private removeCategory(index: number): void {
		this.accountingCategories.splice(index, 1);
	}

	/**
	 * Handle closing the modal, checking for unsaved changes
	 * If there are unsaved changes, show the warning modal
	 *
	 * @return {void}
	 */
	private handleClose(): void {
		const hasUnsavedChanges: boolean = !isEqual(this.accountingCategories, this.restaurant.accounting_categories || []);
		hasUnsavedChanges ? this.showWarningModal = true : this.$emit('close');
	}

	/**
	 * Save changes to accounting categories
	 *
	 * @return {Promise<void>}
	 */
	private async saveChanges(): Promise<void> {
		this.isUpdating = true;
		try {
			await this.updateAccountingCategories(this.accountingCategories);
			this.$emit('close');
		}
		catch (errorMessage) {
			Toast.error(this.$t('settings.accounting_categories.error_saving', { errorMessage }));
		}
		finally {
			this.isUpdating = false;
		}
	}
}
